import React, { useEffect, useRef, useState, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import moment from 'moment'
import Card from '../components/Card/Card'
import { constants } from '../redux/returnCall/actions'
import SweetAlert from 'react-bootstrap-sweetalert'
import { commonConstants } from '../redux/common/actions'
import { getFormattedUser } from '../services/auth'
import ReactTable from 'react-table'
import NotificationSystem from 'react-notification-system'
import { style as notificationStyling } from '../variables/Variables.jsx'
import Button from '../components/CustomButton/CustomButton.jsx'
import CustomReactTablePagination from './Components/CustomReactTablePagination'

const { GET_RETURN_CALLS, DELETE_RETURN_CALL, RESOLVE_RETURN_CALL } = constants;
const { REQUEST } = commonConstants
const headerStyle = { textAlign: 'center', fontWeight: 'bold', color: '#333333' }
const cellStyle = { textAlign: 'center' }

const ReturnCalls = ({ history }) => {
  const { returnCallList, callTakers, loading = {}, success = {}, error = {} } = useSelector(state => state.returnCalls)
  const notificationSystem = useRef()
  const [callTakerId, setCallTakerId] = useState(null)
  const [alert, setAlert] = useState(null)
  const [_notificationSystem, setNotificationSystem] = useState({})

  // initial page fetch
  useEffect(() => {
    dispatch({ type: GET_RETURN_CALLS + REQUEST });
    setNotificationSystem(notificationSystem);
    const returnCallSuccess =
      history.location.state?.type === RESOLVE_RETURN_CALL &&
      history.location.state?.message;
    if (returnCallSuccess) {
      const message = history.location.state.message;
      notificationSystem.current.addNotification({
        message: message,
        level: "info",
        position: "tc",
      });
    }
    return () => {};
  }, []);
  // retrieve admin privilege/user if applicable
  const [hasAdmin, setHasAdmin] = useState(false)
  const [, setSelectedScheduleUser] = useState(null)
  useEffect(() => {
    const getAuth = async() => {
      const user = await getFormattedUser()
      const hasAdmin = user && user.roles && user.roles.includes('SuperAdmin')
      setHasAdmin(hasAdmin)
      setCallTakerId(user.id)
      setSelectedScheduleUser(user.id)
    }
    getAuth().then()
  }, [])
  // handle ajax side effects
  useEffect(() => {
    const notificationSystem = _notificationSystem.current
    if (notificationSystem && error.message) { notificationSystem.addNotification({ message: error.message, level: 'error', position: 'tc' }) }
    return () => {}
  }, [error.message])
  // handle onClick row navigation
  const [historyNavigation, setHistoryNavigation] = useState({ enabled: false, id: null })
  useEffect(() => {
    if (historyNavigation.enabled) {
      history.push(`/admin/return-call/${historyNavigation.id}`)
    }
    return () => {}
  },[historyNavigation.enabled])
  // handle scroll to top for notifications
  useEffect(() => {
    // THIS IS SCREWY - Desktop works fine, Mobile scrolls to top quickly then back to original position - 2x fixed this
    document.getElementById('top-of-the-form-to-ya').scrollIntoView(true)
    document.getElementById('top-of-the-form-to-ya').scrollIntoView(true)
    return () => {}
  }, [success.message, error.message])

  const dispatch = useDispatch()

  const columns = [
    { accessor: 'id', show: false},
    { accessor: 'caller', Header: 'Caller', editable: 'never', searchable: true, style: cellStyle, headerStyle },
    { accessor: 'callTaker', Header: 'Call Taker', editable: 'never', searchable: true, style: cellStyle, headerStyle },
    {
      accessor: 'dateToCall', Header: 'Date to Call', editable: 'never', searchable: true, style: cellStyle, headerStyle,
      Cell: (prop) => moment(prop.value).format('MMM D')
    },
    { accessor: 'phone', Header: 'Phone Number', editable: 'never', searchable: true, style: cellStyle, headerStyle },
    { accessor: 'reason', Header: 'Call Back Details', editable: 'never', searchable: true, style: cellStyle, headerStyle },
    { accessor: 'callFulfilled', show: false }
  ]

  if (hasAdmin) {
    columns.push({
      Header: "Actions",
      accessor: "actions",
      sortable: false,
      filterable: false,
      headerStyle: { textAlign: 'center' }
    })
  }

  const renderDeleteConfirm = (date, callReportId, userId, dispatch) => {
    return setAlert((
      <SweetAlert
        showCancel
        style={{ display: 'block', marginTop: '-100px' }}
        title="Confirm Deletion"
        onConfirm={() => dispatch({ type: DELETE_RETURN_CALL + REQUEST, payload: { callReportId, userId } }) && setAlert(null) }
        onCancel={() => setAlert(null) }
        confirmBtnBsStyle="danger"
      >
        {`Are you sure you want to delete the return call at "${date}"?`}
      </SweetAlert>
    ))
  }

  const filterMethod = (filter, row, column) => {
    const id = filter.pivotId || filter.id
    return row[id] !== undefined ? String(row[id].toLowerCase()).startsWith(filter.value.toLowerCase()) : true
  }

  const list = useMemo(() => {
    return returnCallList.map((prop) => {
      const callTaker = callTakers.find(user => user.id === prop.callTakerId)
      return ({
        id: prop.id,
        caller: prop.name || '-',
        callTaker: (callTaker && (`${callTaker.firstName} ${callTaker.lastName}`)) || '-',
        dateToCall: prop.returnCallDate || '-',
        phone: prop.homePhone || '-',
        reason: prop.safeReturnCallDetails || '-',
        callFulfilled: prop.callFulfilled,
        actions: (
          // we've added some custom button actions
          <div className="actions-center">
            { /* use this button to remove the data row */ }
            <Button
              onClick={() => renderDeleteConfirm(prop.returnCallDate, prop.id, callTakerId, dispatch)}
              bsStyle="danger"
              simple icon>
              <i className="pe-7s-trash action-icon" />
            </Button>
          </div>
        )
      })
    })
  }, [returnCallList])

  return (
    <div className='main-content'>
      <NotificationSystem ref={notificationSystem} style={notificationStyling} />
      {alert}
      <Card
        content={
          <ReactTable
            PaginationComponent={CustomReactTablePagination}
            showTotalPages={true}
            canNextFromData={false}
            data={list}
            filterable
            columns={columns}
            defaultSorted={[{ id: 'dateToCall', desc: true }]}
            defaultFilterMethod={filterMethod}
            defaultPageSize={25}
            showPaginationBottom
            sortable
            loading={loading.source === GET_RETURN_CALLS}
            className="-striped -highlight row-pointer"
            getTrProps={(state, rowInfo) => {
              if (rowInfo && !rowInfo.row.callFulfilled) {
                return { style: { background: 'orange' } }
              }
              else return {}
            }}
            getTdProps={(state, rowInfo, column) => {
              if (column && column.id === 'actions') {
                return {}
              } else if (rowInfo && rowInfo.row) {
                return {
                  onClick: () => setHistoryNavigation({ enabled: true, id: rowInfo.row.id })
                }
              } else {
                return {}
              }
            }}
          />
        }
      />
    </div>
  )
}

export default ReturnCalls